import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getUserLocation,
  handleGeolocationError,
  postDataToAPI,
  postFileDataToAPI,
  setLocalStorageVariable,
} from "../Shared/shared";
import { countryCodesList, homeOwner, tribes } from "../Shared/sharedLists";
import "../CSS/Login.css";
import logo from "../dashboardTemplate/assets/brand/logo2.png";
import nannyImage from "../LandingPage/components/bg/nanny.jpeg";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material";
import MpesaPaymentModal from "../components/MpesaPaymentModal";

const StyledInputField = styled(TextField)({
  width: "100%",
  marginBottom: "32px",
  "& label": {
    fontFamily: "inherit",
    color: "#6F7070",
  },
  "& label.Mui-focused": {
    color: "#DE1877 !important",
  },
  "& .MuiOutlinedInput-root": {
    fontFamily: "inherit",
    borderRadius: "4px",
    "&.Mui-focused fieldset": {
      borderColor: "#642D92",
    },
  },
});

const Register = () => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonText, setButtonText] = useState("Next Step");
  const [fullName, setFullName] = useState(
    localStorage.getItem("fullName") || ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    localStorage.getItem("phoneNumber") || ""
  );
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [password, setPassword] = useState(
    localStorage.getItem("password") || ""
  );
  const [confirmPassword, setConfirmPassword] = useState(
    localStorage.getItem("confirmPassword") || ""
  );
  const [registrationType, setRegistrationType] = useState(
    localStorage.getItem("registrationType") || ""
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [countryCodes, setCountryCodes] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+254");
  const [selectedCountry, setSelectedCountry] = useState(
    localStorage.getItem("selectedCountry") || "254"
  );
  const [termsAccepted, setTermsAccepted] = useState(
    localStorage.getItem("termsAccepted") === "true"
  );
  const [dateOfBirth, setDateOfBirth] = useState(() => {
    return localStorage.getItem("dateOfBirth") || "";
  });
  const [formattedDateOfBirth, setFormattedDateOfBirth] = useState("");
  const [address, setAddress] = useState(localStorage.getItem("address") || "");
  const [idNumber, setIdNumber] = useState(
    localStorage.getItem("idNumber") || ""
  );
  const [selectedImage, setSelectedImage] = useState(
    localStorage.getItem("selectedImage") || null
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [religion, setReligin] = useState(
    localStorage.getItem("religion") || ""
  );
  const [selectedPlaceToReceiveCode, setSelectedPlaceToReceiveCode] = useState(
    localStorage.getItem("selectedPlaceToReceiveCode") || ""
  );
  const [sex, setSex] = useState(localStorage.getItem("sex") || "");
  const [employeeRates, setEmployeeRates] = useState(
    localStorage.getItem("employeeRates") || ""
  );
  const [employerRates, setEmployerRates] = useState(
    localStorage.getItem("employerRates") || ""
  );
  const [selectedTribe, setSelectedTribe] = useState(
    localStorage.getItem("selectedTribe") || ""
  );

  const today = new Date();
  const minDate = new Date(today.setFullYear(today.getFullYear() - 18));
  const minDateString = minDate.toISOString().split("T")[0];

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navigate = useNavigate();

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
        setSelectedImage(base64Image);
        localStorage.setItem("selectedImage", base64Image);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImage && selectedImage.startsWith("blob:")) {
        URL.revokeObjectURL(selectedImage);
      }
    };
  }, [selectedImage]);

  const handleProfilePictureUpdate = async (file) => {
    if (file) {
      const formData = new FormData();
      formData.append("profilePicture", file);
      formData.append("userEmail", email);

      const response = await postFileDataToAPI(
        "/dashboard/updateProfilePicture.php",
        formData
      );

      if (response === "Success") {
        return true;
      } else {
        return false;
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOnSuccess = async () => {
    completeRegistration();
  };

  const handleNextStep = async (e) => {
    e.preventDefault();

    if (
      !fullName ||
      !phoneNumber ||
      !email ||
      !selectedCountry ||
      !password ||
      !registrationType
    ) {
      setErrorMessage("All fields are required.");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    // Strip everything non-digit, combine with country code, check 7–15 digits total
    const normalizedPhone = phoneNumber.replace(/\D/g, "");
    const fullNumber = `${selectedCountryCode}${normalizedPhone}`.replace(/^\+/, "");

    if (!/^\d{7,15}$/.test(fullNumber)) {
      setErrorMessage("Please enter a valid phone number.");
      return;
    }

    const formDataa = new FormData();
    formDataa.append("email", email);
    formDataa.append("phoneNumber", `${selectedCountryCode}${normalizedPhone}`);

    const recordsAlreadyExist = await postDataToAPI(
      "/Auth/checkIfRecordsAlreadyTaken.php",
      formDataa
    );
    if (recordsAlreadyExist !== "success") {
      setErrorMessage(recordsAlreadyExist);
      return;
    }

    handleClick();
    const code = generateVerificationCode();
    setLocalStorageVariable("verificationCode", code);

    let response;
    if (selectedPlaceToReceiveCode === "email") {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("code", code);

      response = await postDataToAPI(
        "/Emails/generateEmailVerificationCode.php",
        formData
      );
      if (response === "Code Sent") {
        setStep(2);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to send verification code. Please try again.");
      }
    } else if (selectedPlaceToReceiveCode === "phone") {
      const formData = new FormData();
      const recipients = fullNumber; // already stripped of + above
      formData.append("recipients", recipients);
      formData.append("phone", normalizedPhone);
      const message = "Hello, Your Nannies Desk Verification Code is: " + code;
      formData.append("message", message);
      formData.append("from", "NanniesDesk Registration Server");

      response = await postDataToAPI("/sms/sendSMS.php", formData);

      if (response.status === "Success") {
        setStep(2);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to send verification code. Please try again.");
      }
    }
  };

  useEffect(() => {
    localStorage.setItem("fullName", fullName);
    localStorage.setItem("phoneNumber", phoneNumber);
    localStorage.setItem("email", email);
    localStorage.setItem("userEmail", email);
    localStorage.setItem("password", password);
    localStorage.setItem("confirmPassword", confirmPassword);
    localStorage.setItem("registrationType", registrationType);
    localStorage.setItem("selectedCountry", selectedCountry);
    localStorage.setItem("termsAccepted", termsAccepted);
    localStorage.setItem("address", address);
    localStorage.setItem("selectedTribe", selectedTribe);
    localStorage.setItem("employerRates", employerRates);
    localStorage.setItem("employeeRates", employeeRates);
    localStorage.setItem("sex", sex);
    localStorage.setItem("religion", religion);
    localStorage.setItem(
      "selectedPlaceToReceiveCode",
      selectedPlaceToReceiveCode
    );
    localStorage.setItem("idNumber", idNumber);
  }, [
    fullName,
    phoneNumber,
    email,
    password,
    confirmPassword,
    registrationType,
    selectedCountry,
    termsAccepted,
    address,
    selectedTribe,
    employerRates,
    employeeRates,
    sex,
    religion,
    idNumber,
    selectedPlaceToReceiveCode,
  ]);

  const clearFormStorage = () => {
    localStorage.removeItem("fullName");
    localStorage.removeItem("phoneNumber");
    localStorage.removeItem("email");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("password");
    localStorage.removeItem("confirmPassword");
    localStorage.removeItem("registrationType");
    localStorage.removeItem("selectedCountry");
    localStorage.removeItem("termsAccepted");
    localStorage.removeItem("dateOfBirth");
    localStorage.removeItem("address");
    localStorage.removeItem("selectedTribe");
    localStorage.removeItem("employeeRates");
    localStorage.removeItem("employerRates");
    localStorage.removeItem("sex");
    localStorage.removeItem("religion");
    localStorage.removeItem("selectedPlaceToReceiveCode");
    localStorage.removeItem("idNumber");
    localStorage.removeItem("selectedImage");
  };

  const completeRegistration = async () => {
    try {
      let latitude = -1.286389;
      let longitude = 36.817223;

      const location = await getUserLocation();
      latitude = location.latitude;
      longitude = location.longitude;

      const normalizedPhone = phoneNumber.replace(/\D/g, "");

      const formData = new FormData();
      formData.append("fullName", fullName);
      formData.append("phoneNumber", `${selectedCountryCode}${normalizedPhone}`);
      formData.append("email", email);
      formData.append("selectedCountry", selectedCountry);
      formData.append("password", password);
      formData.append("coordinates", JSON.stringify([latitude, longitude]));
      formData.append("registrationType", registrationType);
      formData.append("employerRates", employerRates);
      formData.append("employeeRates", employeeRates);
      formData.append("dateOfBirth", dateOfBirth);
      formData.append("address", address);
      formData.append("sex", sex);
      formData.append("religion", religion);
      formData.append("idNumber", idNumber);
      formData.append("tribe", selectedTribe);

      const response = await postDataToAPI("/Auth/signUp.php", formData);
      if (response === "success") {
        let profileImageUpdated = await handleProfilePictureUpdate(selectedFile);
        if (profileImageUpdated) {
          clearFormStorage();
          Swal.fire({
            title: "Success!",
            text: "Registration successful! Redirecting to Login Page",
            icon: "success",
            timer: 3000,
            showConfirmButton: false,
            willClose: () => {
              navigate("/login");
            },
          });
        }
      } else if (response === "Email Already Taken") {
        setErrorMessage("Email already taken.");
      } else {
        console.log("RES: ", response);
        setErrorMessage("Registration failed, please try again later.");
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
      console.error("Error:", error);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode) {
      setErrorMessage("Verification code is required.");
      return;
    }

    const savedCode = localStorage.getItem("verificationCode");
    if (savedCode !== verificationCode) {
      setErrorMessage("Verification code is incorrect.");
      return;
    }

    completeRegistration();
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value);
  };

  const handleClick = () => {
    setIsDisabled(true);
    setButtonText("Sending verification code");

    setTimeout(() => {
      setIsDisabled(false);
      setButtonText("Next Step");
    }, 50000);
  };

  return (
    <>
      <div className={"login-section-container"}>
        <div
          className={"login-left-container"}
          style={{ position: "sticky", top: "0" }}
        >
          <img src={logo} alt={"Logo"} className={"login-logo-image"} />
          <h3 className={"login-left-title"}>
            Trusted Caregiver For Your Little Ones
          </h3>
          <p className={"logo-description"}>
            We have so many satisfied parents who are really happy to take our
            services
          </p>
          <div className={"employer-details"}>
            <p className={"employer-details-text"}>
              "Caregivers are the unsung heroes who pour their hearts into
              nurturing, guiding, and shaping the futures of the little ones
              they embrace"
            </p>
            <div className={"employer-image-container"}>
              <img src={nannyImage} alt={"Employer Image"} />
              <div className={"employer-name"}>
                <p>Veres Panna</p>
                <p>Employer</p>
              </div>
            </div>
          </div>
        </div>
        <div className={"login-right-container"}>
          {step === 1 ? (
            <div className={"register-form-container"}>
              <h3>Register</h3>
              <p className={"register-help-text"}>
                Kindly provide the following details to register an account
              </p>
              <div
                style={{
                  height: isLargeScreen ? "90vh" : "auto",
                  overflowY: isLargeScreen ? "auto" : "visible",
                }}
              >
                <form onSubmit={handleNextStep} style={{ paddingTop: "32px" }}>
                  <StyledInputField
                    required
                    select
                    id={"role-select"}
                    label={"Select who to register as"}
                    slotProps={{
                      select: {
                        native: true,
                      },
                    }}
                    value={registrationType}
                    onChange={(e) => setRegistrationType(e.target.value)}
                  >
                    <option value="">Select who to register as</option>
                    <option value="house-help">House Help (Employee)</option>
                    <option value="home-owner">Home Owner (Employer)</option>
                    <option value="chef">Chef (Employee)</option>
                    <option value="gardener">Gardener (Employee)</option>
                    <option value="care-giver">Caregiver (Employee)</option>
                  </StyledInputField>
                  <StyledInputField
                    required
                    id="outlined-full-name"
                    label="Enter Full Name as per Id"
                    placeholder="Eg. John Kore Doe "
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                    }}
                    type={"text"}
                  />
                  <StyledInputField
                    required
                    id="outlined-required"
                    label="Enter Email"
                    placeholder="Eg. example@gmail.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    type={"email"}
                  />
                  <StyledInputField
                    required
                    id="outlined-phone-number"
                    label="Enter Phone Number"
                    placeholder="Eg. 0712345678"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    type={"tel"}
                  />
                  {registrationType !== homeOwner && (
                    <StyledInputField
                      required
                      id="outlined-employee-rates"
                      label="Enter Monthly Rates(pay)"
                      placeholder="Eg. 20000"
                      value={employeeRates}
                      onChange={(e) => {
                        setEmployeeRates(e.target.value);
                      }}
                      type={"number"}
                    />
                  )}
                  {registrationType === homeOwner && (
                    <StyledInputField
                      required
                      id="outlined-employer-rates"
                      label="Enter your monthly budget"
                      placeholder="Eg. 20000"
                      value={employerRates}
                      onChange={(e) => {
                        setEmployerRates(e.target.value);
                      }}
                      type={"number"}
                    />
                  )}
                  <StyledInputField
                    required
                    id="outlined-password"
                    label="Create Password"
                    placeholder="Eg. 765782"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    type={"password"}
                  />
                  <StyledInputField
                    required
                    id="outlined-confirm-password"
                    label="Confirm Password"
                    placeholder="Eg. 765782"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                    }}
                    type={"password"}
                  />
                  <StyledInputField
                    required
                    select
                    id={"code-select"}
                    label={""}
                    slotProps={{
                      select: {
                        native: true,
                      },
                    }}
                    value={selectedPlaceToReceiveCode}
                    onChange={(e) =>
                      setSelectedPlaceToReceiveCode(e.target.value)
                    }
                  >
                    <option value="">Select Where to receive code</option>
                    <option value="email">On your Email</option>
                    <option value="phone">Phone Via Text</option>
                  </StyledInputField>
                  <div className="" style={{ textAlign: "left" }}>
                    <label>Select Profile Photo:</label>
                    <br />
                    <input
                      required
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="upload-button"
                      style={{
                        width: "60%",
                        marginRight: "10px",
                        marginTop: "16px",
                        marginBottom: "16px",
                      }}
                    />
                    {selectedImage && (
                      <div style={{ marginTop: "10px" }}>
                        <p>Selected Image:</p>
                        <img
                          src={selectedImage}
                          alt="Selected"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>{" "}
                  <br></br>
                  <div
                    className="formGroup"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      required
                    />
                    <span style={{ marginLeft: "8px" }}>
                      I accept the <Link to="/terms">Terms and Services</Link>
                    </span>
                  </div>
                  {errorMessage && (
                    <p className="error-message">{errorMessage}</p>
                  )}
                  {successMessage && (
                    <p className="success-message">{successMessage}</p>
                  )}
                  <button
                    type="submit"
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: isDisabled ? "#aaa" : "#DE1877",
                      color: "white",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "50px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      fontSize: "16px",
                    }}
                    disabled={isDisabled}
                  >
                    {buttonText}
                  </button>
                  <div style={{ color: "green", marginTop: "10px" }}>
                    <Link to="/login" className="link-register" id="link">
                      Already have an Account? Login
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className={"code-submission-container"}>
              <h3>Register</h3>
              <form
                onSubmit={handleCodeSubmit}
                className={"code-submission-form"}
              >
                <p
                  className="register-title"
                  style={{ textAlign: "left", marginBottom: "20px" }}
                >
                  {selectedPlaceToReceiveCode === "email" ? (
                    <p>Check your email for the verification code</p>
                  ) : (
                    <p>Check your phone for the verification code</p>
                  )}
                </p>
                <div className={"verify-code-image"} />
                <StyledInputField
                  required
                  id="outlined-verification-code"
                  label="Verification Code"
                  placeholder="Enter Verification Code"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value);
                  }}
                  type={"text"}
                />
                {errorMessage && (
                  <p
                    className="error-message"
                    style={{ color: "red", textAlign: "center" }}
                  >
                    {errorMessage}
                  </p>
                )}
                <button type={"submit"} className={"login-form-button"}>
                  {selectedPlaceToReceiveCode === "email" ? (
                    <div>Verify Email</div>
                  ) : (
                    <div>Verify Phone</div>
                  )}
                </button>
                <div
                  className="login-links"
                  style={{ textAlign: "center", marginTop: "15px" }}
                >
                  <Link
                    to="/login"
                    className="link-register"
                    style={{ textDecoration: "none", color: "#007BFF" }}
                  >
                    Already have an Account? Login
                  </Link>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <MpesaPaymentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleOnSuccess}
        connectId={0}
        regType={"completeProfile"}
      />
    </>
  );
};

export default Register;